import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { Modal, Button, Header, Rating, Message, Icon, Card, Image } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';

import {
  isReviewResponsePending,
  isReviewResponseChanged,
  isReviewResponseSubmitted,
  getAuthorTextPhoto,
} from './ReviewsTableModal.utils';
import CustomResponse from './components/CustomResponse/CustomResponse';
import PreparedResponse from './components/PreparedResponse/PreparedResponse';
import API from '../../../../../libs/api';
import { formatLocaleDate } from '../../../../../libs/date-utils/date-formatting';
import { getErrorMessage } from '../../../../../libs/errors';
import CloseableModal from '../../../../common/CloseableModal';
import { MAX_STAR_RATING } from '../../constants';
import { reviewShape } from '../../types';
import { getReviewResponse, isReviewResponseEmpty } from '../ReviewsTable/ReviewsTable.utils';
import { getPublishAt } from '../ReviewsTableRow/ReviewsTableRow.utils';

import './ReviewsTableModal.scss';

const propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  review: reviewShape,
};

const defaultProps = {
  review: null,
};

export default function ReviewsTableModal({ open, onClose, review }) {
  if (_isEmpty(review)) {
    return null;
  }

  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [replyText, setReplyText] = useState('');

  const { reviewDate, author, rating, text, authorPhotoUrl } = review;
  const reviewResponse = getReviewResponse(review);
  const publishAt = getPublishAt(review);
  const isPending = isReviewResponsePending(reviewResponse);
  const isSubmitted = isReviewResponseSubmitted(reviewResponse);
  const replyChanged = isReviewResponseChanged(reviewResponse, replyText);

  const authorPlaceholderPhotoUrl = getAuthorTextPhoto(author);

  function handleClose() {
    setIsEditing(false);
    onClose();
  }

  async function handleSaveAndPublish() {
    try {
      setIsSaving(true);
      if (replyText) {
        await API.updateReviewResponse(reviewResponse.id, { content: replyText });
      }

      await API.publishReviewResponse(reviewResponse.id);
      handleClose();
    } catch (err) {
      setErrorMessage('Failed to save and submit reply. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleResponseUpdate() {
    try {
      setIsSaving(true);
      await API.updateReviewResponse(reviewResponse.id, { content: replyText });
      handleClose();
    } catch (err) {
      setErrorMessage(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    const isEmpty = isReviewResponseEmpty(reviewResponse);

    if (isEmpty) {
      setIsEditing(true);
      setReplyText('');
    } else {
      const { content } = reviewResponse;
      setIsEditing(false);
      setReplyText(content);
    }
  }, [reviewResponse]);

  const showSaveButton = !_isEmpty(publishAt);
  const disabledSaveButton = !replyText || !replyChanged || isSaving;

  if (!open) {
    return null;
  }

  return (
    <CloseableModal
      open={open}
      onClose={handleClose}
      header="Respond to Review"
      className="review-table-modal"
    >
      <Modal.Content>
        <div className="reply-modal-content">
          <div className="review-details">
            <Card fluid>
              <Card.Content>
                <Image
                  floated="right"
                  size="mini"
                  src={authorPhotoUrl || authorPlaceholderPhotoUrl}
                  referrerPolicy="no-referrer"
                  circular
                />
                <Card.Header>
                  <Header as="h4">{author}</Header>
                </Card.Header>
                <Card.Meta>
                  <Rating maxRating={MAX_STAR_RATING} rating={rating} size="small" disabled />
                </Card.Meta>
                <Card.Meta className="date">
                  {reviewDate ? ` Submitted on ${formatLocaleDate(reviewDate)}` : 'No date'}
                </Card.Meta>
                <Card.Description className="text">{text}</Card.Description>
              </Card.Content>
            </Card>
          </div>

          <PreparedResponse
            reviewResponse={reviewResponse}
            isEditing={isEditing}
            isPending={isPending}
            setReplyText={setReplyText}
            setIsEditing={setIsEditing}
            isSubmitted={isSubmitted}
          />

          <CustomResponse
            reviewResponse={reviewResponse}
            isEditing={isEditing}
            replyText={replyText}
            setReplyText={setReplyText}
            setIsEditing={setIsEditing}
            isSubmitted={isSubmitted}
          />
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={handleClose}>
          Close
        </Button>
        {!isSubmitted && (
          <>
            {showSaveButton && (
              <Button
                basic
                primary
                onClick={handleResponseUpdate}
                disabled={disabledSaveButton}
                loading={isSaving}
              >
                <Icon name="save" />
                Save
              </Button>
            )}
            <Button
              primary
              onClick={handleSaveAndPublish}
              disabled={!replyText || isSaving}
              loading={isSaving}
            >
              <Icon name="paper plane" />
              {replyChanged ? 'Save and Submit' : 'Submit'}
            </Button>
          </>
        )}
      </Modal.Actions>
      {errorMessage && <Message negative>{errorMessage}</Message>}
    </CloseableModal>
  );
}

ReviewsTableModal.propTypes = propTypes;
ReviewsTableModal.defaultProps = defaultProps;
